import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Wifi, Check, Loader2, Home, Mail, Phone, User, Copy } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import PrivacyPolicyModal from '../components/PrivacyPolicyModal';
import TermsOfServiceModal from '../components/TermsOfServiceModal';
import FeaturedPromoCard from '../components/FeaturedPromoCard';
import ArrivalKitDisplay from '../components/ArrivalKitDisplay';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WiFiAccessPage = () => {
  const { t } = useTranslation();
  const { unitCode } = useParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [unitInfo, setUnitInfo] = useState(null);
  const [wifiCredentials, setWifiCredentials] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(false);

  useEffect(() => {
    fetchUnitInfo();
  }, [unitCode]);

  const fetchUnitInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE}/wifi-access/unit/${unitCode}`);
      setUnitInfo(response.data);
    } catch (error) {
      console.error('Error fetching unit info:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast({
        title: 'Missing information',
        description: 'Please fill in all fields',
        variant: 'destructive'
      });
      return;
    }
    
    if (!agreedToTerms) {
      toast({
        title: 'Agreement Required',
        description: 'Please agree to the Privacy Policy and Terms of Service to continue',
        variant: 'destructive'
      });
      return;
    }

    try {
      setLoading(true);
      
      const response = await axios.post(`${API_BASE}/wifi-access/request`, {
        ...formData,
        unit_code: unitCode
      });

      if (response.data.success) {
        setWifiCredentials(response.data.wifi_credentials);
        setSubmitted(true);
        
        toast({
          title: 'Success! 🎉',
          description: response.data.message,
        });
      } else {
        toast({
          title: 'WiFi Not Available',
          description: response.data.message,
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error submitting WiFi request:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to get WiFi credentials',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied!',
      description: `${label} copied to clipboard`,
    });
  };

  if (submitted && wifiCredentials) {
    // Determine building for location-based content
    const propertyInfo = wifiCredentials.property_info || unitInfo;
    const building = propertyInfo?.title?.toLowerCase().includes('decarie') ? 'Decarie' : 
                     propertyInfo?.title?.toLowerCase().includes('denis') ? 'St-Denis' : 
                     'Montreal';

    const handleExploreStays = () => {
      // Redirect to homepage with building parameter for future targeted ads
      window.location.href = `/?building=${building}&source=wifi`;
    };

    // Local recommendations by building
    const stDenisRecommendations = [
      { name: "Boulangerie Le Toledo", type: "Bakery", icon: "🥖" },
      { name: "Sammi & Soupe Dumpling", type: "Asian Cuisine", icon: "🥟" },
      { name: "Nouilles de Lanzhou", type: "Chinese Noodles", icon: "🍜" },
      { name: "Ibéricos Taverne à Tapas", type: "Spanish Tapas", icon: "🇪🇸" },
      { name: "Rôtisserie Piri Piri", type: "Portuguese Grill", icon: "🍗" },
      { name: "Piel Canela", type: "Latino Brunch", icon: "🥞" },
      { name: "Café Myriade", type: "Coffee Shop", icon: "☕" },
      { name: "DEK SEN", type: "Thai Cuisine", icon: "🍛" }
    ];

    const decarieRecommendations = [
      { name: "Coming Soon", type: "We're adding local favorites!", icon: "📍" }
    ];

    const recommendations = building === 'St-Denis' ? stDenisRecommendations : decarieRecommendations;

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-rose-500 to-pink-500 text-white px-6 py-3 rounded-full mb-4">
              <Home className="w-6 h-6" />
              <span className="text-xl font-bold">Homesbyli</span>
            </div>
          </div>

          {/* Success Card */}
          <Card className="p-8 shadow-xl border-2 border-green-200 bg-white">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Welcome to {unitInfo?.title || 'Your Unit'}!
              </h2>
              <p className="text-gray-600">
                Thank you! Here are your WiFi credentials:
              </p>
            </div>

            {/* WiFi Credentials */}
            <div className="space-y-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Wifi className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">Network Name (SSID)</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(wifiCredentials.ssid, 'Network name')}
                    className="h-8"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-lg font-bold text-gray-900 font-mono">
                  {wifiCredentials.ssid}
                </p>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Password</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(wifiCredentials.password, 'Password')}
                    className="h-8"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-lg font-bold text-gray-900 font-mono">
                  {wifiCredentials.password}
                </p>
              </div>
            </div>

            {/* Connection Instructions */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="font-semibold text-gray-900 mb-2">How to Connect:</h3>
              <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                <li>Open WiFi settings on your device</li>
                <li>Select the network: <span className="font-mono font-semibold">{wifiCredentials.ssid}</span></li>
                <li>Enter the password shown above</li>
                <li>You're connected! Enjoy your stay 🎉</li>
              </ol>
            </div>

            {/* Featured promo CTA — visible once the WiFi handshake is done */}
            <FeaturedPromoCard apiBase={API_BASE} brandUrl={window.location.origin} />

            {/* Digital arrival kit — pulls per-property + per-group config
                and renders door code, checkout, parking etc. plus locked
                teasers for the signup-gated rewards. */}
            <ArrivalKitDisplay
              unitCode={unitCode}
              customerToken={localStorage.getItem('token')}
            />

            {/* Local Recommendations */}
            {building === 'St-Denis' && recommendations.length > 0 && (
              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 border-2 border-orange-200 rounded-lg p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🍽️</span>
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg">
                      Local Favorites Near You
                    </h3>
                    <p className="text-sm text-gray-600">
                      Must-visit restaurants & cafés in Plateau
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 gap-2">
                  {recommendations.map((place, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-lg p-3 hover:shadow-md transition-shadow border border-orange-100"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{place.icon}</span>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 text-sm">
                            {place.name}
                          </p>
                          <p className="text-xs text-gray-500">{place.type}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 pt-4 border-t border-orange-200">
                  <p className="text-xs text-gray-600 text-center">
                    💡 Tap any restaurant to open in Google Maps
                  </p>
                </div>
              </div>
            )}

            {/* Location-Based Promotional Banner */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-lg mb-4 text-white text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 rounded-full mb-3">
                <Home className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold mb-2">
                Staying in {building}?
              </h3>
              <p className="text-sm mb-4 text-blue-100">
                🎉 <span className="font-semibold">Book directly with us</span> next time and <span className="font-semibold">save 15-20%</span> on booking platform fees!
              </p>
              <p className="text-xs text-blue-200 mb-4">
                Discover more amazing properties in Montreal
              </p>
              <Button
                onClick={handleExploreStays}
                className="w-full bg-white text-blue-600 hover:bg-blue-50 font-semibold text-base py-6"
                size="lg"
              >
                🏠 Explore Our Properties
              </Button>
            </div>

            {/* Secondary Info */}
            <div className="text-center">
              <p className="text-xs text-gray-500">
                💡 Get exclusive deals and early access to new properties
              </p>
            </div>
          </Card>

          <p className="text-center text-sm text-gray-500 mt-4">
            Enjoy your stay in Montreal! 🍁
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-rose-500 to-pink-500 text-white px-6 py-3 rounded-full mb-4">
            <Home className="w-6 h-6" />
            <span className="text-xl font-bold">Homesbyli</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome to {unitInfo?.title || 'Your Unit'}!
          </h1>
          <p className="text-gray-600">
            Get instant WiFi access by providing your details below
          </p>
        </div>

        {/* Form Card */}
        <Card className="p-8 shadow-xl">
          <div className="flex items-center gap-3 mb-6 pb-6 border-b">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Wifi className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">WiFi Access</h2>
              <p className="text-sm text-gray-600">Unit: {unitCode}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 123-4567"
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-900">
              <p className="font-medium mb-1">Why do we need this?</p>
              <p className="text-blue-800">
                In compliance with Canadian privacy laws (PIPEDA & Quebec's Law 25), your data is used solely for identity verification and to enhance your stay.
              </p>
            </div>

            {/* Privacy Policy & Terms Agreement */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms-agreement"
                  checked={agreedToTerms}
                  onCheckedChange={setAgreedToTerms}
                  className="mt-1"
                />
                <label
                  htmlFor="terms-agreement"
                  className="text-sm text-gray-700 leading-relaxed cursor-pointer"
                >
                  I have read and agree to the{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowPrivacyPolicy(true);
                    }}
                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                  >
                    Privacy Policy
                  </button>
                  {' '}and{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowTermsOfService(true);
                    }}
                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                  >
                    Terms of Service
                  </button>
                  . By connecting, you consent to the collection and use of your information in compliance with Quebec's Law 25 and Canada's PIPEDA.
                </label>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
              size="lg"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Getting WiFi Credentials...
                </>
              ) : (
                <>
                  <Wifi className="w-5 h-5 mr-2" />
                  Get WiFi Password
                </>
              )}
            </Button>
          </form>
        </Card>

        <p className="text-center text-xs text-gray-500 mt-4">
          By accessing WiFi, you agree to our{' '}
          <button
            onClick={() => setShowTermsOfService(true)}
            className="text-blue-600 hover:underline"
          >
            Terms of Service
          </button>
          {' '}and{' '}
          <button
            onClick={() => setShowPrivacyPolicy(true)}
            className="text-blue-600 hover:underline"
          >
            Privacy Policy
          </button>
        </p>
      </div>

      {/* Legal Modals */}
      <PrivacyPolicyModal
        isOpen={showPrivacyPolicy}
        onClose={() => setShowPrivacyPolicy(false)}
      />
      <TermsOfServiceModal
        isOpen={showTermsOfService}
        onClose={() => setShowTermsOfService(false)}
      />
    </div>
  );
};

export default WiFiAccessPage;
